<template>
  <a-input
    v-model:value="innerValue"
    type="url"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="maxlength"
    :status="computedStatus"
    class="ant-url-input"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i class="fas fa-link" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  maxlength?: number;
  status?: "" | "error" | "warning";
  validateOnBlur?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "https://example.com",
  disabled: false,
  size: "middle",
  allowClear: true,
  maxlength: 2048,
  status: "",
  validateOnBlur: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "change", value: string): void;
  (e: "validate", valid: boolean): void;
}>();

const isValidUrl = (url: string) => {
  if (!url) return true;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  return !!props.modelValue && !isValidUrl(props.modelValue);
});

const computedStatus = computed(() => {
  if (props.status) return props.status;
  return localError.value ? "error" : "";
});

const innerValue = computed({
  get: () => props.modelValue ?? "",
  set: (val: string) => {
    emit("update:modelValue", val);
    emit("change", val);
  },
});

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidUrl(props.modelValue ?? ""));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — URL
  Global: <h-url v-model="website" />
  Import: import { UrlInput } from '@/components/ant/input'

  Props: modelValue, placeholder, disabled, size, allowClear, maxlength, status, validateOnBlur
  Events: update:modelValue, change, blur, pressEnter, validate
  Docs: src/components/ant/input/README.md
-->
